import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompany } from 'models/companies'
import { ContactCompanyType } from 'models/contact_companies'
import { DealsPipeline } from 'models/deals_pipeline'
import { useCallback } from 'react'

type CompanyRequiredFields = {
  id: number
  company_type?: ContactCompanyType
  chain?: string
  university_id?: number
  k12district_id?: number
  lodging_id?: number
}

type PrefixMap = Record<string, string> | undefined

/**
 * Pure function - gets the url for the company based on the type
 */
export const getCompanyUrl = (
  company: CompanyRequiredFields | ContactCompany,
  prefixMap: PrefixMap
): string => {
  if (!company || !prefixMap) return '-'

  const { company_type, chain, id, university_id, k12district_id, lodging_id } =
    company

  switch (company_type) {
    case ContactCompanyType.Chain:
      return `/chain/${prefixMap[company_type]}${chain}`
    case ContactCompanyType.University:
      return `/university/${prefixMap[company_type]}${university_id}`
    case ContactCompanyType.K12District:
      return `/k12/${prefixMap[company_type]}${k12district_id}`
    case ContactCompanyType.Lodging:
      return `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
    case ContactCompanyType.UserGenerated:
      return `/company/${prefixMap[company_type]}${id}`
    default:
      return `/company/${prefixMap[ContactCompanyType.UserGenerated]}${id}`
  }
}

/**
 * Hook wrapper - returns a function that gets the company url
 */
export const useCompanyUrl = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (company: CompanyRequiredFields | ContactCompany) =>
      getCompanyUrl(company, prefixMap),
    [prefixMap]
  )
}

/**
 * Pure function - gets the url for the company based on v2 params.
 *
 * Optional `searchParams` get appended as `?key=value`. Existing two-arg
 * callers keep working unchanged.
 */
export const getCompanyV2Url = (
  params: {
    company_type?: string
    chain_id?: number
    university_id?: number
    k12district_id?: number
    lodging_id?: number
    id?: number
  },
  prefixMap: PrefixMap,
  searchParams?: Record<string, string | number>
): string => {
  if (!prefixMap) return '-'
  const {
    company_type,
    chain_id,
    university_id,
    k12district_id,
    lodging_id,
    id,
  } = params

  let path: string
  switch (company_type) {
    case ContactCompanyType.Chain:
      path = `/chain/${prefixMap[company_type]}${chain_id}`
      break
    case ContactCompanyType.University:
      path = `/university/${prefixMap[company_type]}${university_id}`
      break
    case ContactCompanyType.K12District:
      path = `/k12/${prefixMap[company_type]}${k12district_id}`
      break
    case ContactCompanyType.Lodging:
      path = `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
      break
    case ContactCompanyType.UserGenerated:
      path = `/company/${prefixMap[company_type]}${id}`
      break
    default:
      path = `/company/${prefixMap[ContactCompanyType.UserGenerated]}${id}`
  }

  if (!searchParams || Object.keys(searchParams).length === 0) return path
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(searchParams)) {
    qs.set(key, String(value))
  }
  return `${path}?${qs.toString()}`
}

/**
 * Hook wrapper - returns a function that gets the company v2 url
 */
export const useCompanyV2Url = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (
      params: Parameters<typeof getCompanyV2Url>[0],
      searchParams?: Parameters<typeof getCompanyV2Url>[2]
    ) => getCompanyV2Url(params, prefixMap, searchParams),
    [prefixMap]
  )
}

/**
 * Pure function - gets the url for the company from a deal pipeline record
 */
export const getCompanyUrlFromDealPipeline = (
  dealPipelineRecord: DealsPipeline,
  prefixMap: PrefixMap
): string => {
  if (!dealPipelineRecord || !prefixMap) return '-'
  const {
    company_type,
    chain_id,
    contact_company_id,
    university_id,
    k12district_id,
    nursinghome_id,
    lodging_id,
  } = dealPipelineRecord

  switch (company_type) {
    case ContactCompanyType.Chain:
      return `/chain/${prefixMap[company_type]}${chain_id}`
    case ContactCompanyType.University:
      return `/university/${prefixMap[company_type]}${university_id}`
    case ContactCompanyType.K12District:
      return `/k12/${prefixMap[company_type]}${k12district_id}`
    case ContactCompanyType.NursingHome:
      return `/nursinghome/${prefixMap[company_type]}${nursinghome_id}`
    case ContactCompanyType.UserGenerated:
      return `/company/${prefixMap[company_type]}${contact_company_id}`
    case ContactCompanyType.Lodging:
      return `/lodging/chain/${prefixMap[company_type]}${lodging_id}`
    default:
      return `/company/${prefixMap[ContactCompanyType.UserGenerated]}${contact_company_id}`
  }
}

/**
 * Hook wrapper - returns a function that gets the company url from a deal pipeline record
 */
export const useCompanyUrlFromDealPipeline = () => {
  const { preferences } = usePreferences()
  const prefixMap = preferences?.company_type_prefix_map
  return useCallback(
    (dealPipelineRecord: DealsPipeline) =>
      getCompanyUrlFromDealPipeline(dealPipelineRecord, prefixMap),
    [prefixMap]
  )
}
